import type { ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { Check, ChevronRight, Pencil, Trash2, X } from "lucide-react";
import { observer } from "mobx-react-lite";

import { cn } from "@/utils/shadcn-ui";

import type { DataModel } from "../../../../models/data-model";
import type { ColumnFilterSpec } from "./filter-toolbar/types";
import type {
  DataTableColumnDef,
  DataTableFeatures,
  DataTableCellContext,
  DataTableColumn,
  DataTableFilterFn,
  DataTableHeaderContext,
  DataTableInstance,
  DataTableRow,
} from "./table-features";
import type { Column, EditMode } from "./types";

import { assert } from "../../../../utils/utils";
import { HelperTooltip } from "../../base/helper-tooltip";
import { useFormContext } from "../../form/form";
import { IconButton } from "../../icon-button";
import { CellDeleteButton } from "../cells/cell-delete-button";
import { EditableCell, editableFields } from "./editable-cell";
import { sortingFnFor } from "./filter-fns";
import { useRowIndex } from "./row-index-context";
import { SortHeader } from "./sort-header";
import { filterFieldForColumn, getColumnId } from "./types";

export interface BuildColumnsParams<T extends object> {
  columns: Column<T>[];
  model: DataModel<T>;
  canSelect?: "single" | "multi";
  /** Tooltip text explaining what selecting rows does (multi-select only). */
  selectionHint?: ReactNode;
  /** Predicate disabling a row's selection checkbox/radio (e.g. implied rows). */
  selectDisabled?: (item: T) => boolean;
  readOnly?: boolean;
  /** Prepend a pinned expander column (the table was given `getSubRows`). */
  expandable?: boolean;
  showEditColumn: boolean;
  showDeleteColumn: boolean;
  editMode: EditMode;
  multiEdit?: boolean;
  onDelete?: (row: T) => void | Promise<unknown>;
  confirmDelete?: (row: T) => Promise<boolean>;
  onDialogEdit: (item: T) => void;
  onEdit?: (patch: Partial<T>, row: T) => void | Promise<unknown>;
}

export function buildColumns<T extends object>(
  params: BuildColumnsParams<T>,
): {
  columns: DataTableColumnDef<T>[];
  pinnedStart: string[];
  pinnedEnd: string[];
} {
  const {
    columns,
    expandable,
    canSelect,
    selectionHint,
    showEditColumn,
    showDeleteColumn,
  } = params;

  // Leaves are collected with their group name and nested into group defs at
  // the end (see `nestGroups`), so pinning/ordering logic stays leaf-based.
  const result: LeafEntry<T>[] = [];
  const pinnedStart: string[] = [];
  const pinnedEnd: string[] = [];
  // Column id must equal `String(attr)` for the filter system to resolve the
  // column (filter fields look it up via `table.getColumn(field.id)`), so two
  // columns sharing an `attr` would collide into one TanStack column and break
  // their independent sort/filter. Warn loudly in dev rather than failing
  // silently with duplicate-key React warnings.
  if (import.meta.env.DEV) {
    const seen = new Set<string>();
    for (let i = 0; i < columns.length; i++) {
      const id = getColumnId(columns[i], i);
      if (seen.has(id)) {
        console.warn(
          `DataTable: duplicate column id "${id}" — two columns share the same \`attr\`. Their sorting/filtering will collide.`,
        );
      }
      seen.add(id);
    }
  }

  if (expandable) {
    pinnedStart.push("__expand");
    result.push({
      def: {
        id: "__expand",
        size: 40,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        header: EmptyHeader,
        cell: ExpandRowCell,
      },
    });
  }

  if (canSelect) {
    pinnedStart.push("__select");
    result.push({
      def: {
        id: "__select",
        // A little extra width so the absolutely-positioned hint icon has room.
        size: selectionHint && canSelect === "multi" ? 60 : 44,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        meta: { build: params },
        header: SelectColumnHeader,
        cell: SelectColumnCell,
      },
    });
  }

  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    const id = getColumnId(col, i);
    const sticky = col.sticky;
    if (sticky) {
      // `Column.sticky` stays physical (`left`/`right`) — it's the public API
      // ~40 tables are written against, and the table isn't RTL-aware. This is
      // the one place it maps onto v9's logical `start`/`end` pinning.
      if (sticky === "right") {
        pinnedEnd.push(id);
      } else {
        pinnedStart.push(id);
      }
    }

    const filterFn = filterFnForSpec(col.filter, col);
    const filterField = filterFieldForColumn(col);
    // A group boundary is wherever the group changes between adjacent columns
    // (either side may be ungrouped); the divider is drawn on its left edge.
    const groupStart = i > 0 && col.group !== columns[i - 1].group;
    result.push({
      group: col.group,
      def: {
        id,
        accessorKey: col.attr as string | undefined,
        size: col.width,
        enableSorting: !!col.sort,
        enableColumnFilter: !!col.filter,
        // Sticky columns anchor the horizontal scroll, so they always render —
        // the column-visibility panel shows them checked and disabled.
        enableHiding: !sticky,
        sortFn: sortingFnFor<DataTableFeatures, T>(
          col.type,
          col.sortTiebreakBy,
        ),
        filterFn,
        meta: { column: col, groupStart, filterField, build: params },
        header: DataColumnHeader,
        cell: DataColumnCell,
      },
    });
  }

  if (showEditColumn) {
    pinnedEnd.push("__edit");
    result.push({
      def: {
        id: "__edit",
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        meta: { build: params },
        header: EditColumnHeader,
        cell: EditColumnCell,
      },
    });
  }

  if (showDeleteColumn) {
    pinnedEnd.push("__delete");
    result.push({
      def: {
        id: "__delete",
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        meta: { build: params },
        header: DeleteColumnHeader,
        cell: DeleteColumnCell,
      },
    });
  }

  return { columns: nestGroups(result), pinnedStart, pinnedEnd };
}

interface LeafEntry<T extends object> {
  def: DataTableColumnDef<T>;
  group?: string;
}

/**
 * Wraps runs of adjacent leaves that share a `group` into TanStack group column
 * defs, which is what makes the table render a second header row above the
 * column headers. Ungrouped leaves stay top-level.
 */
function nestGroups<T extends object>(
  leaves: LeafEntry<T>[],
): DataTableColumnDef<T>[] {
  const out: DataTableColumnDef<T>[] = [];
  let openGroup: string | undefined;
  let openColumns: DataTableColumnDef<T>[] = [];
  for (const leaf of leaves) {
    if (!leaf.group) {
      openGroup = undefined;
      out.push(leaf.def);
      continue;
    }
    if (leaf.group === openGroup) {
      openColumns.push(leaf.def);
      continue;
    }
    openGroup = leaf.group;
    openColumns = [leaf.def];
    const groupHeader = leaf.def.meta?.column?.groupHeader;
    out.push({
      // Keyed on the span's first leaf, not the group name: a group interrupted
      // by another group renders as two spans (see `Column.group`), and those
      // would otherwise collide on one id.
      id: `__group-${leaf.def.id ?? leaf.group}`,
      header: groupHeader != null ? GroupHeader : leaf.group,
      // The group header carries its first column's divider, so the line runs
      // unbroken from the group row down through the body.
      meta: { groupStart: leaf.def.meta?.groupStart, groupHeader },
      columns: openColumns,
    });
  }
  return out;
}

/*
 * Header and cell renderers. TanStack renders a function as a component, so
 * they are defined once here rather than inline: rebuilt column defs then
 * re-render them instead of remounting them, which would reset their state
 * (an open header filter, say).
 */

function DataColumnHeader<T extends object>({
  column,
  table,
}: DataTableHeaderContext<T>) {
  const col = dataColumnOf(column);
  const filterField = column.columnDef.meta?.filterField;
  return (
    <SortHeader
      column={column}
      sortable={!!col.sort}
      filter={filterField && { table, field: filterField }}
    >
      {col.header}
    </SortHeader>
  );
}

function DataColumnCell<T extends object>({
  column,
  row,
}: DataTableCellContext<T>) {
  const { model, editMode, multiEdit, onEdit } = buildOf(column);
  return (
    <EditableCell
      item={row.original}
      column={dataColumnOf(column)}
      model={model}
      editMode={editMode}
      multiEdit={multiEdit}
      onEdit={onEdit}
    />
  );
}

function SelectColumnHeader<T extends object>({
  column,
  table,
}: DataTableHeaderContext<T>) {
  const { model, readOnly, selectionHint } = buildOf(column);
  return (
    <SelectAllHeader
      model={model}
      table={table}
      readOnly={readOnly}
      hint={selectionHint}
    />
  );
}

function SelectColumnCell<T extends object>({
  column,
  row,
}: DataTableCellContext<T>) {
  const { model, readOnly, selectDisabled } = buildOf(column);
  return (
    <SelectRowCell
      model={model}
      row={row.original}
      disabled={readOnly}
      selectDisabled={selectDisabled}
    />
  );
}

function EditColumnHeader() {
  return (
    <SortHeader sortable={false}>
      <Trans>Edit</Trans>
    </SortHeader>
  );
}

function EditColumnCell<T extends object>({
  column,
  row,
}: DataTableCellContext<T>) {
  const { model, editMode, multiEdit, columns, onDialogEdit } = buildOf(column);
  return (
    <EditActionCell
      item={row.original}
      model={model}
      editMode={editMode}
      multiEdit={multiEdit}
      columns={columns}
      onDialogEdit={onDialogEdit}
    />
  );
}

function EmptyHeader() {
  return null;
}

function GroupHeader<T>({ column }: { column: DataTableColumn<T> }) {
  return column.columnDef.meta?.groupHeader;
}

function ExpandRowCell<T>({ row }: { row: DataTableRow<T> }) {
  const { t } = useLingui();
  if (!row.getCanExpand()) return null;
  const expanded = row.getIsExpanded();
  return (
    <IconButton
      icon={ChevronRight}
      size={16}
      label={expanded ? t`Collapse row` : t`Expand row`}
      tooltip={null}
      aria-expanded={expanded}
      // The bare IconButton's brand magenta, like the table's other row
      // actions; only the rotation is added.
      className={cn("transition-transform", expanded && "rotate-90")}
      onClick={row.getToggleExpandedHandler()}
    />
  );
}

function DeleteColumnHeader() {
  return (
    <SortHeader sortable={false}>
      <Trans>Delete</Trans>
    </SortHeader>
  );
}

function DeleteColumnCell<T extends object>({
  column,
  row,
}: DataTableCellContext<T>) {
  const { model, onDelete, confirmDelete } = buildOf(column);
  return (
    <DeleteActionCell
      item={row.original}
      model={model}
      onDelete={onDelete}
      confirmDelete={confirmDelete}
    />
  );
}

function buildOf<T>(column: DataTableColumn<T>) {
  const build = column.columnDef.meta?.build;
  assert(build, `Column "${column.id}" was not built by buildColumns`);
  return build;
}

function dataColumnOf<T>(column: DataTableColumn<T>) {
  const col = column.columnDef.meta?.column;
  assert(col, `Column "${column.id}" has no column config`);
  return col;
}

const EditActionCell = observer(function EditActionCell<T extends object>({
  item,
  model,
  editMode,
  multiEdit,
  columns,
  onDialogEdit,
}: {
  item: T;
  model: DataModel<T>;
  editMode: EditMode;
  multiEdit?: boolean;
  columns: Column<T>[];
  onDialogEdit: (item: T) => void;
}) {
  const { t } = useLingui();
  // In dialog mode the edit column normally opens the dialog — but if this row
  // is being inline-edited (an external flow set editingItemId, e.g. the
  // medications "Change dosage" button), fall through to the Update/Cancel
  // buttons instead.
  const id = model.idAttr != null ? item[model.idAttr] : undefined;
  if (!multiEdit && editMode === "dialog" && model.editingItemId !== id) {
    const validationMessage = model.validateItemEdit(item);
    return (
      <IconButton
        icon={Pencil}
        variant="subtle"
        label={t`Edit`}
        // Why the button is disabled is the useful hover text here; with
        // nothing to report the button carries no tooltip, as before.
        tooltip={validationMessage ?? null}
        disabled={!!validationMessage}
        onClick={() => onDialogEdit(item)}
      />
    );
  }

  return (
    <EditButtons
      model={model}
      item={item}
      fields={editableFields(columns, item)}
      multiEdit={multiEdit}
    />
  );
});

/**
 * Row-mode edit/cancel/save buttons keyed by `item[idAttr]` rather than by
 * row index — DataTable's rendered rows can be sorted/filtered/paginated
 * differently from `model.paginatedData`, so any index lookup against the
 * model is unreliable here.
 */
const EditButtons = observer(function EditButtons<T extends object>({
  model,
  item,
  fields,
  multiEdit,
}: {
  model: DataModel<T>;
  item: T;
  fields: (keyof T)[];
  multiEdit?: boolean;
}) {
  const { t } = useLingui();
  const id = model.idAttr != null ? item[model.idAttr] : undefined;
  const editing = model.editingItemId === id;
  const validationMessage = model.validateItemEdit(item);
  const saving = !!useFormContext()?.pending;

  if (multiEdit) {
    if (model.isMultiEditing(id)) {
      return (
        <IconButton
          icon={X}
          variant="subtle"
          label={t`Cancel`}
          disabled={saving}
          onClick={() => model.stopMultiEditing(id)}
        />
      );
    }

    return (
      <IconButton
        icon={Pencil}
        variant="subtle"
        label={t`Edit`}
        // As in `EditActionCell`, why the button is disabled is the useful
        // hover text. It replaces the `data-tooltip` this used to carry: base
        // `Button` stays hoverable when disabled, so the CSS bubble would now
        // show underneath the one `label` brings.
        tooltip={validationMessage ?? null}
        disabled={!!validationMessage || saving}
        onClick={() => model.startMultiEditing(item, fields)}
      />
    );
  }

  if (editing) {
    return (
      <>
        <IconButton
          icon={Check}
          variant="subtle"
          type="submit"
          label={t`Update`}
        />{" "}
        <IconButton
          icon={X}
          variant="subtle"
          label={t`Cancel`}
          onClick={() => model.stopEditing()}
        />
      </>
    );
  }

  return (
    <IconButton
      icon={Pencil}
      variant="subtle"
      label={t`Edit`}
      tooltip={validationMessage ?? null}
      disabled={!!validationMessage || saving || model.editingIndex === -1}
      onClick={() => model.startEditingItem(item, fields)}
    />
  );
});

const DeleteActionCell = observer(function DeleteActionCell<T extends object>({
  item,
  model,
  onDelete,
  confirmDelete,
}: {
  item: T;
  model: DataModel<T>;
  onDelete?: (row: T) => void | Promise<unknown>;
  confirmDelete?: (row: T) => Promise<boolean>;
}) {
  const index = useRowIndex();
  return (
    <CellDeleteButton
      index={() => index}
      item={item}
      model={model}
      onConfirm={async () => {
        await onDelete?.(item);
      }}
      requestConfirm={confirmDelete && (() => confirmDelete(item))}
    >
      <Trash2 size={16} className="text-foreground" />
    </CellDeleteButton>
  );
});

const SelectAllHeader = observer(function SelectAllHeader<T extends object>({
  model,
  table,
  readOnly,
  hint,
}: {
  model: DataModel<T>;
  table: DataTableInstance<T>;
  readOnly?: boolean;
  hint?: ReactNode;
}) {
  const { t } = useLingui();
  if (readOnly || model.selectionMode !== "multi") return null;
  // Select what's actually visible in the table, not what model.sortFilterData
  // returns — TanStack owns filtering/sorting in DataTable.
  const visible = table.getFilteredRowModel().rows.map((r) => r.original);
  const selectedCount = visible.filter(
    (item) => model.idAttr != null && model.isSelected(item[model.idAttr]),
  ).length;
  const allSelected = visible.length > 0 && selectedCount === visible.length;
  return (
    // The checkbox stays centered in the cell; the hint icon is positioned
    // absolutely beside it so adding it doesn't shift the checkbox.
    <span className="relative inline-flex items-center justify-center">
      <input
        type="checkbox"
        aria-label={t`Select all`}
        checked={allSelected}
        ref={(el) => {
          // Indeterminate is a DOM property only, not an HTML attribute.
          if (el) el.indeterminate = selectedCount > 0 && !allSelected;
        }}
        onChange={() => {
          if (allSelected) {
            model.deselectAll();
          } else {
            model.selectAllItems(visible);
          }
        }}
      />
      {hint && (
        <HelperTooltip
          className="absolute top-1/2 left-full ml-1 -translate-y-1/2"
          content={hint}
        />
      )}
    </span>
  );
});

const SelectRowCell = observer(function SelectRowCell<T extends object>({
  model,
  row,
  disabled,
  selectDisabled,
}: {
  model: DataModel<T>;
  row: T;
  disabled?: boolean;
  selectDisabled?: (item: T) => boolean;
}) {
  const id = model.idAttr != null ? row[model.idAttr] : undefined;
  // Read `selectDisabled` here (inside the observer) so a predicate derived
  // from `model.selected` re-evaluates whenever the selection changes.
  const isDisabled = disabled || (selectDisabled?.(row) ?? false);

  if (model.selectionMode === "single") {
    return (
      <input
        type="radio"
        checked={model.isSelected(id)}
        onChange={() => model.select(id, true)}
        disabled={isDisabled}
      />
    );
  }

  if (model.selectionMode === "multi") {
    return (
      <input
        type="checkbox"
        checked={model.isSelected(id)}
        onChange={() => {
          if (model.isSelected(id)) {
            model.deselect(id);
          } else {
            model.select(id, false);
          }
        }}
        disabled={isDisabled}
      />
    );
  }

  return null;
});

function filterFnForSpec<T>(
  spec: ColumnFilterSpec<T> | undefined,
  col: Column<T>,
):
  | "enumIn"
  | "enumInArray"
  | "textWithOperator"
  | DataTableFilterFn<T>
  | undefined {
  if (!spec) return undefined;
  if (spec.kind === "text") return "textWithOperator";
  if (spec.kind === "multi") {
    if (spec.predicate) {
      const predicate = spec.predicate;
      return (row, _columnId, filterValue) => {
        const selected = (filterValue as unknown[] | undefined) ?? [];
        if (selected.length === 0) return true;
        return predicate(row.original, selected);
      };
    }
    return col.type === "array" ? "enumInArray" : "enumIn";
  }
  // bool — closure over predicate
  const predicate = spec.predicate;
  return (row, _columnId, filterValue) => {
    if (filterValue !== true) return true;
    return predicate(row.original);
  };
}
